import React from "react";

// The two orders listPopularGuests ranks in. "viewers": most different people
// opened their profile. "joinRate": of the people who opened it before joining
// an event, the share who then joined. `short` is what a phone shows.
const SORTS = [
  { key: "viewers", label: "Most viewed", short: "Views" },
  { key: "joinRate", label: "Join rate" },
];

/**
 * Why someone is missing from (or low on) the join-rate order. The server
 * floors thin samples and says where it set the floor, so the number here is
 * always the one it used.
 */
export function joinRateFloorNote(scope, minBeforeJoining) {
  if (!minBeforeJoining) return null;
  return scope === "enrolled"
    ? `Members with fewer than ${minBeforeJoining} people who looked before joining come last, by people viewed.`
    : `Only members with at least ${minBeforeJoining} people who looked before joining are ranked, so one lucky join can't top the list.`;
}

/**
 * "Rank by": the order of the Popular list. Shared by the operator tab and the
 * host view, which rank the same way. The label goes on a phone, where the two
 * buttons say enough on their own.
 */
export function PopularSortToggle({ sort, onChange }) {
  return (
    <div className="flex items-center gap-2">
      <span className="hidden sm:inline text-xs text-gray-500 dark:text-gray-400 shrink-0">Rank by</span>
      <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 p-1 rounded-lg">
        {SORTS.map((s) => (
          <button
            key={s.key}
            type="button"
            onClick={() => onChange(s.key)}
            aria-pressed={sort === s.key}
            aria-label={s.label}
            className={`px-2 sm:px-3 py-1 rounded-md text-xs sm:text-sm font-medium whitespace-nowrap transition-colors ${sort === s.key
              ? "bg-white dark:bg-gray-900 shadow text-gray-900 dark:text-gray-100"
              : "text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-200"
              }`}
          >
            {s.short ? (
              <>
                <span className="sm:hidden">{s.short}</span>
                <span className="hidden sm:inline">{s.label}</span>
              </>
            ) : s.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * The row above the Popular list: the order, and "only people nobody has
 * messaged yet". One row, so the list starts higher on a phone; the checkbox
 * says less there and wraps under the toggle only if it still has to.
 */
export function PopularListControls({ sort, onSortChange, unmessagedOnly, onUnmessagedChange, skippedMessaged, isLoading }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 mb-3">
      <PopularSortToggle sort={sort} onChange={onSortChange} />
      <label className="flex items-center gap-2 text-xs sm:text-sm text-gray-700 dark:text-gray-200">
        <input
          type="checkbox"
          checked={unmessagedOnly}
          onChange={(e) => onUnmessagedChange(e.target.checked)}
          disabled={isLoading}
          className="rounded border-gray-300 dark:border-gray-600"
        />
        <span>
          <span className="sm:hidden">Not messaged</span>
          <span className="hidden sm:inline">Only people nobody has messaged yet</span>
          {unmessagedOnly && !isLoading && skippedMessaged > 0 && (
            <span className="text-xs text-gray-500 dark:text-gray-400"> · {skippedMessaged} hidden</span>
          )}
        </span>
      </label>
    </div>
  );
}

export default PopularSortToggle;
